import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Calendar } from "@untitledui/icons";
import { type ReactNode, useEffect, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import {
	DetailField,
	DetailFieldGrid,
	DetailFieldSection,
} from "@/components/base/detail-field/detail-field";
import { TabHeader } from "@/components/base/tab-header";
import { EmptyValue } from "@/components/common/EmptyValue";
import type { HistoryEntry } from "@/components/Organisms/HistoryList";
import { HistoryList } from "@/components/Organisms/HistoryList";
import { useAppTranslation } from "@/hooks/use-app-translation";
import type { AuditLogDisplay } from "@/services/job/job.types";
import { organizationQueryOptions } from "@/services/organization";
import { getEntityAuditDisplayFields } from "@/utils/entity-audit-display";
import { formatLongDate } from "@/utils/utils";
import { SearchSchema } from "../$organizationId";

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/organizations/$organizationId/activity",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/organizations/$organizationId/activity",
	});
	const organizationId = params.organizationId;

	const orgById = useQuery(organizationQueryOptions.detail(organizationId));
	const { createdByUser, updatedByUser } = getEntityAuditDisplayFields(
		orgById.data,
	);

	const pageSize = 10;
	const historyCountQuery = useQuery(
		organizationQueryOptions.history.count(organizationId),
	);

	const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
		useInfiniteQuery(
			organizationQueryOptions.history.infinite(
				organizationId,
				pageSize,
				historyCountQuery.data,
			),
		);

	const historyEntries: HistoryEntry[] = useMemo(() => {
		if (!data?.pages) return [];
		return data.pages.flat().map(
			(log: AuditLogDisplay): HistoryEntry => ({
				id: log.id,
				action: log.action,
				date: log.date,
				fieldName: log.fieldName,
				oldValue: log.oldValue,
				newValue: log.newValue,
				user: log.user
					? {
							id: log.user.id,
							displayName: log.user.displayName,
							email: log.user.email,
							firstName: log.user.firstName,
							middleName: log.user.middleName,
							lastName: log.user.lastName,
						}
					: null,
				entityType: log.entityType,
			}),
		);
	}, [data]);

	const { ref: loadMoreRef, inView } = useInView({
		threshold: 0,
		rootMargin: "0px 0px 300px 0px",
	});

	useEffect(() => {
		if (inView && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

	return (
		<div className="space-y-6">
			<TabHeader title={t("admin.organizations.drawer.activity")} />

			<div className="bg-(--color-bg-secondary) rounded-lg p-4">
				<div className="space-y-4">
					<DetailFieldGrid>
						<DetailFieldSection>
							<ActivityField
								label={t("admin.organizations.activity.created").toUpperCase()}
								value={orgById.data?.createdAt}
							/>
							<DetailField
								label={t(
									"admin.organizations.activity.createdBy",
								).toUpperCase()}
								valueClassName="font-bold text-gray-900"
								value={
									createdByUser ? (
										<span className="font-bold text-gray-900">
											{createdByUser}
										</span>
									) : (
										<EmptyValue />
									)
								}
							/>
						</DetailFieldSection>
						<DetailFieldSection>
							<ActivityField
								label={t(
									"admin.organizations.activity.lastUpdate",
								).toUpperCase()}
								value={orgById.data?.updatedAt}
							/>
							<DetailField
								label={t(
									"admin.organizations.activity.lastUpdateBy",
								).toUpperCase()}
								valueClassName="font-bold text-gray-900"
								value={
									updatedByUser ? (
										<span className="font-bold text-gray-900">
											{updatedByUser}
										</span>
									) : (
										<EmptyValue />
									)
								}
							/>
						</DetailFieldSection>
					</DetailFieldGrid>
				</div>
			</div>

			<div className="space-y-3 rounded-lg border border-gray-200 bg-white p-6">
				<div>
					<h4 className="text-lg font-semibold text-gray-900">
						{t("admin.users.tabs.activity.history.title")}
					</h4>
					<p className="text-sm text-gray-500">
						{t("admin.users.tabs.activity.history.description")}
					</p>
				</div>
				<HistoryList
					entries={historyEntries}
					isLoading={isLoading}
					emptyMessage={t("admin.users.tabs.activity.history.empty")}
					hasNextPage={hasNextPage}
					isFetchingNextPage={isFetchingNextPage}
					loadMoreRef={loadMoreRef}
					located="jobs"
				/>
			</div>
		</div>
	);
}

function ActivityField({
	label,
	value,
	includeTime,
}: {
	label: string;
	value: string | null | undefined;
	includeTime?: boolean;
}): ReactNode {
	return (
		<DetailField
			label={label}
			valueClassName="font-bold text-gray-900"
			value={
				value ? (
					<div className="flex items-center gap-2">
						<Calendar size={15} className="text-gray-400" />
						{formatLongDate(
							value,
							includeTime ? { includeTime: true } : undefined,
						)}
					</div>
				) : (
					<EmptyValue />
				)
			}
		/>
	);
}
